import { RealtimeHealthService } from '@services/realtime/realtime-health.service'
import { Injectable } from '@angular/core'
import { BehaviorSubject, EMPTY, Observable, Subject } from 'rxjs'
import { IMessage } from '@stomp/stompjs'
import { ChatPresence, ChatTypingPayload, ChatWsEvent, ChatParticipantRole } from '@models/chat/chat.model'
import { StompConnectionService } from '@services/socket/stomp-connection.service'

export type SocketConnectionStatus = 'connecting' | 'connected' | 'disconnected'

export interface ChatRoomSession {
	events$: Observable<ChatWsEvent>
	typing$: Observable<ChatTypingPayload>
	presence$: Observable<ChatPresence>
	status$: Observable<SocketConnectionStatus>
	sendTyping(state: boolean): void
	heartbeat(): void
	disconnect(): void
}

@Injectable({ providedIn: 'root' })
export class ChatSocketService {
	constructor(
		private stomp: StompConnectionService,
		private health: RealtimeHealthService
	) {}

	/**
	 * Sala do participante ou do agente, na conexão da aba (`StompConnectionService`). A saída da sala vai em
	 * `{active: false}` no `disconnect`: a conexão da aba segue aberta pelas outras leases (lances, fase), e o servidor
	 * não vê desconexão nenhuma. Se a aba fecha, o servidor tira a sessão das salas em que o heartbeat dela foi aceito.
	 */
	createRoomSession(roomId: string, tenantId: string, role: ChatParticipantRole): ChatRoomSession {
		const events$ = new Subject<ChatWsEvent>()
		const typing$ = new Subject<ChatTypingPayload>()
		const presence$ = new Subject<ChatPresence>()
		const connectionStatus$ = new BehaviorSubject<SocketConnectionStatus>('connecting')
		const lease = this.stomp.acquire({ tenantId, roleId: `${role}` })

		const announcePresence = (active = true) =>
			lease.publish({
				destination: `/app/chatrooms/${roomId}/${role}/presence`,
				body: JSON.stringify({ active })
			})

		lease.subscribe(`/topic/chatrooms.${roomId}`, frame => this.forwardEvent(frame, events$))
		lease.subscribe(`/topic/typing.${roomId}`, frame => this.forwardTyping(frame, typing$))
		lease.subscribe(`/topic/presence.${roomId}`, frame => this.forwardPresence(frame, presence$))

		// A cada (re)conexão, depois de a lease reassinar a sala.
		const status = lease.status$.subscribe(state => {
			connectionStatus$.next(state)
			this.health.markStatus('chat', state)
			if (state === 'connected') announcePresence()
		})

		return {
			events$: events$.asObservable(),
			typing$: typing$.asObservable(),
			presence$: presence$.asObservable(),
			status$: connectionStatus$.asObservable(),
			sendTyping: state => {
				lease.publish({
					destination: `/app/chatrooms/${roomId}/${role}/typing`,
					body: JSON.stringify({ typing: state })
				})
			},
			heartbeat: () => {
				announcePresence()
			},
			disconnect: () => {
				// Sem este aviso, quem fechava o chat seguia online para os demais até eles recarregarem (28/10/2026).
				announcePresence(false)
				// Antes do `release`: ele marca a lease `disconnected` na hora, e a tela que está trocando de sala leria
				// isso como queda — o chat ficava em "Sincronizando com o servidor..." na sala seguinte (27/10/2026).
				status.unsubscribe()
				lease.release()
				this.health.markStatus('chat', 'disconnected')
				events$.complete()
				typing$.complete()
				presence$.complete()
				connectionStatus$.complete()
			}
		}
	}

	/**
	 * Sessão do visitante (spec 048, RF 29): só lê o espelho `tenant.{t}.visitor-chat.{roomId}`, com as mensagens já
	 * tratadas pelo sigilo e em lista branca. Sem digitação, presença nem papel na sala: o visitante não aparece para
	 * ninguém, e o servidor nega a ele os tópicos da sala.
	 */
	createReaderSession(roomId: string, tenantId: string): ChatRoomSession {
		const events$ = new Subject<ChatWsEvent>()
		const connectionStatus$ = new BehaviorSubject<SocketConnectionStatus>('connecting')
		const lease = this.stomp.acquire({ tenantId })

		lease.subscribe(`/topic/tenant.${tenantId}.visitor-chat.${roomId}`, frame => this.forwardEvent(frame, events$))

		const status = lease.status$.subscribe(state => {
			connectionStatus$.next(state)
			this.health.markStatus('chat', state)
		})

		return {
			events$: events$.asObservable(),
			typing$: EMPTY,
			presence$: EMPTY,
			status$: connectionStatus$.asObservable(),
			sendTyping: () => undefined,
			heartbeat: () => undefined,
			disconnect: () => {
				// Antes do `release`, pelo mesmo motivo da sala do participante.
				status.unsubscribe()
				lease.release()
				this.health.markStatus('chat', 'disconnected')
				events$.complete()
				connectionStatus$.complete()
			}
		}
	}

	private forwardEvent(frame: IMessage, sink: Subject<ChatWsEvent>) {
		try {
			// Frame recebido = canal vivo. É a única prova de entrega que o cliente tem: com broker em
			// memória por pod, a conexão continua 'connected' mesmo sem nada chegar (docs/realtime-stomp.md).
			// Só o evento conta — digitando/presença são ruído constante e mascarariam o silêncio real.
			this.health.markFrame('chat')
			sink.next(JSON.parse(frame.body) as ChatWsEvent)
		} catch {
			/* noop */
		}
	}

	private forwardTyping(frame: IMessage, sink: Subject<ChatTypingPayload>) {
		try {
			const { payload } = JSON.parse(frame.body)
			if (payload) {
				sink.next(payload as ChatTypingPayload)
			}
		} catch {
			/* noop */
		}
	}

	private forwardPresence(frame: IMessage, sink: Subject<ChatPresence>) {
		try {
			const { payload } = JSON.parse(frame.body)
			if (payload) {
				sink.next(payload as ChatPresence)
			}
		} catch {
			/* noop */
		}
	}
}
